import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { BufferedChangeset } from 'ember-changeset/types';
import CustomFieldDefinition, {
  buildValidations,
  buildValues
} from 'volta/models/custom-field-definition';
import {
  TCustomFieldTableValue,
  TCustomFieldValues
} from 'volta/models/types/customFieldDefinition';

import { action } from '@ember/object';
import { next } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

interface IArgs {
  definitions: CustomFieldDefinition[];
  values: TCustomFieldValues;
  onValidate?: (isInvalid: boolean) => void;
  onChange?: (values: TCustomFieldValues, isInvalid: boolean) => void;
  valuesBySlug?: boolean;
}

export default class CustomFieldsEditor extends Component<IArgs> {
  // Services
  // ~~~~~

  @tracked declare changeset: BufferedChangeset;
  @tracked values?: TCustomFieldValues = this.args.values;

  // Properties
  // ~~~~~

  validations: object = {};
  definitionsById: Record<string, CustomFieldDefinition> = {};

  // Computed Properties
  // ~~~~~

  get valuesById() {
    const values: TCustomFieldValues = {};
    const { definitions } = this.args;
    definitions.forEach((d) => {
      values[d.id] = this.changeset.get(d.slug);
    });
    return values;
  }

  get valuesBySlug() {
    const values: TCustomFieldValues = {};
    const { definitions } = this.args;
    definitions.forEach((d) => {
      values[d.slug] = this.changeset.get(d.slug);
    });
    return values;
  }

  // Lifecycle Hooks
  // ~~~~~

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.initChangeset();
  }

  // Actions
  // ~~~~~

  @action
  handleValuesChange() {
    next(() => {
      this.initChangeset();
    });
  }

  @action
  handleSingleValueChange(slug: string, value: TCustomFieldTableValue) {
    this.changeset.set(slug, value);
    this.submit();
  }

  @action
  async validate() {
    await this.changeset.validate();
    await this.args.onValidate?.(this.changeset.isInvalid);
  }

  // Helper Functions
  // ~~~~~

  /**
   * Initialises the Changeset state
   */
  initChangeset() {
    this.validations = buildValidations(this.args.definitions);
    this.values = buildValues(this.args.values, this.args.definitions);
    this.definitionsById = {};
    this.args.definitions.forEach((d) => {
      this.definitionsById[d.id] = d;
    });
    const changeset = Changeset(this.values, lookupValidator(this.validations), this.validations);
    this.changeset = changeset;
    next(() => {
      this.validate();
    });
  }

  async submit() {
    await this.changeset.validate();
    this.args.onChange?.(
      this.args.valuesBySlug ? this.valuesBySlug : this.valuesById,
      this.changeset.isInvalid
    );
  }
}
